/**
 * TokenGrid — Responsive grid of TokenCards with infinite scroll.
 *
 * - Loading state: 8 shimmer skeleton cards
 * - Empty state: icon + message + Clear Filters CTA
 * - Infinite scroll via IntersectionObserver on a sentinel element
 * - Stagger animation on grid load
 *
 * Story 3.4 — Task 3
 */

import type { TokenListItem } from "@repo/shared-types";
import { motion } from "framer-motion";
import { useEffect, useRef } from "react";

import { staggerContainer } from "../../lib/animations";
import { EmptyState } from "../ui/EmptyState";
import { Spinner } from "../ui/Spinner";
import { TokenCard } from "./TokenCard";

type TokenGridProps = {
	tokens: TokenListItem[];
	isLoading: boolean;
	isFetchingNextPage: boolean;
	hasNextPage: boolean;
	fetchNextPage: () => void;
	onClearFilters: () => void;
	hasActiveFilters: boolean;
};

function SkeletonCard() {
	return (
		<div className="rounded-xl border border-pastel-border bg-white p-4 animate-pulse">
			<div className="flex items-start gap-2.5 mb-3">
				<div className="h-10 w-10 rounded-full bg-pastel-light-gray flex-shrink-0" />
				<div className="flex-1 space-y-1.5">
					<div className="h-4 w-24 bg-pastel-light-gray rounded" />
					<div className="h-3 w-12 bg-pastel-light-gray rounded" />
				</div>
			</div>
			<div className="flex items-center justify-between mb-2">
				<div className="h-6 w-20 bg-pastel-light-gray rounded" />
				<div className="h-4 w-14 bg-pastel-light-gray rounded" />
			</div>
			<div className="flex items-center justify-between">
				<div className="h-5 w-16 bg-pastel-light-gray rounded-full" />
				<div className="h-3 w-16 bg-pastel-light-gray rounded" />
			</div>
		</div>
	);
}

function LoadingGrid() {
	return (
		<div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
			{Array.from({ length: 8 }, (_, i) => (
				<SkeletonCard key={`skeleton-${String(i)}`} />
			))}
		</div>
	);
}

function SearchIcon() {
	return (
		<svg
			className="h-12 w-12"
			fill="none"
			viewBox="0 0 24 24"
			strokeWidth={1.5}
			stroke="currentColor"
			role="img"
			aria-label="Search"
		>
			<title>Search</title>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
			/>
		</svg>
	);
}

export function TokenGrid({
	tokens,
	isLoading,
	isFetchingNextPage,
	hasNextPage,
	fetchNextPage,
	onClearFilters,
	hasActiveFilters,
}: TokenGridProps) {
	const sentinelRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const sentinel = sentinelRef.current;
		if (!sentinel || !hasNextPage) return;

		const observer = new IntersectionObserver(
			(entries) => {
				const entry = entries[0];
				if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
					fetchNextPage();
				}
			},
			{ rootMargin: "200px" }
		);

		observer.observe(sentinel);
		return () => observer.disconnect();
	}, [hasNextPage, isFetchingNextPage, fetchNextPage]);

	if (isLoading) {
		return <LoadingGrid />;
	}

	if (tokens.length === 0) {
		return (
			<EmptyState
				title="No tokens found"
				description={
					hasActiveFilters
						? "Try adjusting your search or filters to find what you're looking for."
						: "No tokens have been launched yet. Be the first!"
				}
				icon={<SearchIcon />}
				action={
					hasActiveFilters ? (
						<button
							type="button"
							onClick={onClearFilters}
							className="px-4 py-2 text-sm font-medium text-pastel-vibrant-blue hover:text-pastel-vibrant-blue/80 transition-colors"
						>
							Clear Filters
						</button>
					) : undefined
				}
			/>
		);
	}

	return (
		<>
			<motion.div
				variants={staggerContainer}
				initial="hidden"
				animate="visible"
				className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
			>
				{tokens.map((token) => (
					<TokenCard key={token.id} token={token} />
				))}
			</motion.div>

			{/* Infinite scroll sentinel */}
			<div ref={sentinelRef} className="h-1" />

			{isFetchingNextPage && (
				<div className="flex justify-center py-8">
					<Spinner size="md" />
				</div>
			)}
		</>
	);
}
